import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where
} from 'firebase/firestore'
import { db } from '@/firebase'
import { isWorkspaceRotatorEmail } from '@/utils/workspaceRoleRotator'
import { attempt, accessDetail } from '../ruleExpectations.js'
import { rotateDemoRoleInSession } from '../roleRotate.js'

// One probe per smoke run. It signs in as admin to seed a form, reads it as a
// service provider, then restores the role the runner was on. Repeating that
// for every rotator role would only refresh the same token over and over.
const ranForRun = new Set()

const OTHER = 'smoke-other-teacher'

export default {
  id: 'feedbackViewerShare',
  label: 'Feedback view-only share',
  description:
    'A service provider listed in viewerIds can read the form and its responses. One who is not listed cannot.',
  destructive: true,

  async run(ctx, step) {
    if (!isWorkspaceRotatorEmail(ctx.user?.email)) {
      step.skip(
        'viewer share',
        'Needs the demo rotator (admin@casemanagevue.com) to seed a form and then read it as a service provider.'
      )
      return
    }
    if (ranForRun.has(ctx.runId)) {
      step.skip('viewer share', 'Already checked earlier in this run.')
      return
    }
    ranForRun.add(ctx.runId)

    const originRole = ctx.roleClaim
    const uid = ctx.user.uid
    const school = ctx.schoolId ? { schoolId: ctx.schoolId } : {}
    const tag = { __smokeTest: true, __smokeRunId: ctx.runId }
    const sharedInst = doc(collection(db, 'feedbackInstances'))
    const hiddenInst = doc(collection(db, 'feedbackInstances'))
    const sharedResp = doc(db, 'feedbackResponses', `${sharedInst.id}_${OTHER}`)
    const hiddenResp = doc(db, 'feedbackResponses', `${hiddenInst.id}_${OTHER}`)
    const studentRef = doc(collection(db, 'students'))
    let seeded = false

    const baseInst = {
      assignedTeachers: [OTHER],
      status: 'completed',
      responderCloseMode: 'manual',
      caseManagerId: uid,
      studentId: studentRef.id,
      studentName: 'SMOKE Feedback viewer',
      templateTitle: 'SMOKE viewer share',
      audienceType: 'student',
      ...tag,
      ...school
    }

    try {
      await step('seed a shared form as admin', async () => {
        if (originRole !== 'admin') await rotateDemoRoleInSession('admin')
        seeded = true
        await setDoc(studentRef, { ...tag, ...school, app: { studentData: { plan: 'IEP' } } })
        await setDoc(sharedInst, { ...baseInst, viewerIds: [uid] })
        await setDoc(hiddenInst, { ...baseInst, viewerIds: [OTHER] })
        const responseBase = {
          teacherId: OTHER,
          studentId: baseInst.studentId,
          caseManagerId: uid,
          status: 'submitted',
          responses: { note: 'smoke' },
          ...tag,
          ...school
        }
        await setDoc(sharedResp, {
          ...responseBase,
          instanceId: sharedInst.id,
          viewerIds: [uid]
        })
        await setDoc(hiddenResp, {
          ...responseBase,
          instanceId: hiddenInst.id,
          viewerIds: [OTHER]
        })
        seeded = true
        return `feedbackInstances/${sharedInst.id}`
      })

      await step('service provider in viewerIds can read the form', async () => {
        await rotateDemoRoleInSession('service_provider')
        const { allowed } = await attempt(() => getDoc(sharedInst))
        return accessDetail(true, allowed)
      })

      await step('service provider in viewerIds can read the responses', async () => {
        const { allowed } = await attempt(() => getDoc(sharedResp))
        return accessDetail(true, allowed)
      })

      if (ctx.schoolId) {
        await step('service provider can list forms shared with them', async () => {
          const { allowed } = await attempt(() =>
            getDocs(
              query(
                collection(db, 'feedbackInstances'),
                where('schoolId', '==', ctx.schoolId),
                where('viewerIds', 'array-contains', uid)
              )
            )
          )
          return accessDetail(true, allowed)
        })
      }

      await step('service provider not in viewerIds cannot read the form', async () => {
        const { allowed } = await attempt(() => getDoc(hiddenInst))
        return accessDetail(false, allowed)
      })

      await step('service provider not in viewerIds cannot read the responses', async () => {
        const { allowed } = await attempt(() => getDoc(hiddenResp))
        return accessDetail(false, allowed)
      })
    } finally {
      try {
        if (seeded) {
          await rotateDemoRoleInSession('admin')
          await deleteDoc(sharedResp).catch(() => {})
          await deleteDoc(hiddenResp).catch(() => {})
          await deleteDoc(sharedInst).catch(() => {})
          await deleteDoc(hiddenInst).catch(() => {})
          await deleteDoc(studentRef).catch(() => {})
        }
      } finally {
        if (originRole && originRole !== 'admin') {
          await rotateDemoRoleInSession(originRole).catch(() => {})
        }
      }
    }
  }
}
